"use client";
import { useCallback, useEffect, useState } from "react"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

// --- Lib ---
import { isNodeTypeSelected } from "@/tiptap/lib/tiptap-utils"

// --- Icons ---
import { DirectionLtrIcon } from "@/tiptap/icons/direction-ltr-icon"
import { DirectionRtlIcon } from "@/tiptap/icons/direction-rtl-icon"

export const textDirectionIcons = {
  ltr: DirectionLtrIcon,
  rtl: DirectionRtlIcon,
}

export const textDirectionLabels = {
  ltr: "Left to right",
  rtl: "Right to left",
}

export function canSetTextDirection(editor, direction) {
  if (!editor || !editor.isEditable) return false
  if (!["ltr", "rtl"].includes(direction)) return false
  if (isNodeTypeSelected(editor, ["image", "horizontalRule"])) return false

  return editor.can().updateAttributes("paragraph", { dir: direction });
}

export function isTextDirectionActive(editor, direction) {
  if (!editor || !editor.isEditable) return false
  return editor.isActive("paragraph", { dir: direction });
}

export function setTextDirection(editor, direction) {
  if (!editor || !editor.isEditable) return false
  if (!canSetTextDirection(editor, direction)) return false

  return editor
    .chain()
    .focus()
    .updateAttributes("paragraph", { dir: direction })
    .run();
}

export function shouldShowButton({
  editor,
  direction,
  hideWhenUnavailable,
}) {
  if (!editor) return false

  if (!hideWhenUnavailable) {
    return true
  }

  if (!editor.isEditable) return false
  if (editor.isActive("code")) return true

  return canSetTextDirection(editor, direction);
}

export function useTextDirection(config) {
  const {
    editor: providedEditor,
    direction,
    hideWhenUnavailable = false,
    onDirected,
  } = config

  const { editor } = useTiptapEditor(providedEditor)
  const [isVisible, setIsVisible] = useState(true)
  const canSetDirection = canSetTextDirection(editor, direction)
  const isActive = isTextDirectionActive(editor, direction)

  useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      setIsVisible(shouldShowButton({ editor, direction, hideWhenUnavailable }))
    }

    handleSelectionUpdate()

    editor.on("selectionUpdate", handleSelectionUpdate)

    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate)
    };
  }, [editor, direction, hideWhenUnavailable])

  const handleTextDirection = useCallback(() => {
    if (!editor) return false

    const success = setTextDirection(editor, direction)
    if (success) {
      onDirected?.()
    }
    return success
  }, [editor, direction, onDirected])

  return {
    isVisible,
    isActive,
    handleTextDirection,
    canSetDirection,
    label: textDirectionLabels[direction],
    Icon: textDirectionIcons[direction],
  }
}
